<template>
  <!-- Only inside the desktop app, and only for a tender from a SEAO notice.
       Looks and sits like the extension's button (DownloadDocumentsButton),
       whose tooltip style it reuses; says what it's doing while it runs. -->
  <div v-if="isAvailable" class="fetch-documents-button desktop-download-documents">
    <span v-if="running" class="desktop-download-documents__progress">{{ progressLabel }}</span>
    <v-popover
      v-if="!isTenderActive"
      placement="top"
      trigger="hover"
      popoverClass="tender-bottom-nav__nav-tooltip"
      :delay="{ show: 100, hide: 600 }"
    >
      <BaseRoundButton
        class="desktop-download-documents-button"
        :size="36"
        is-transparent
        icon="file-input"
        disabled
        :aria-label="$t('page.opportunity-details.document-manipulations.fetch-from-seao')"
      />
      <template v-slot:popover>
        <div class="fetch-documents-button__tooltip">{{ $t('desktop-robot.active-status-only') }}</div>
      </template>
    </v-popover>
    <BaseRoundButton
      v-else
      class="desktop-download-documents-button"
      :size="36"
      is-transparent
      icon="file-input"
      :tooltip-text="$t('page.opportunity-details.document-manipulations.fetch-from-seao')"
      tooltipPlacement="top"
      :is-loading="running"
      @click.native="downloadDocuments"
    />
  </div>
</template>

<script>
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import { mapActions, mapGetters } from 'vuex';
import { desktopSeaoRobot, documentsToUpload, isSeaoNoticeUrl, uploadName } from '@/util/desktop-robot';
import { isPdfFile } from '@/util/document-file-util';

// Downloads a SEAO tender's documents with the desktop app's robot
// (aloha.desktop.shell), then uploads them to this opportunity with the
// page's own session. The browser extension's button (DownloadDocumentsButton)
// does the same job on the web; the extension doesn't run in the desktop app.
export default {
  name: 'DesktopDownloadDocumentsButton',
  components: { BaseRoundButton },
  props: {
    opportunity: { type: Object, required: true },
  },
  data() {
    return { running: false, progress: null, unsubscribe: null };
  },
  computed: {
    ...mapGetters('tender', ['tender']),
    // As the extension's button: the loaded tender's status, or the one the
    // opportunity carries while it loads.
    isTenderActive() {
      const tender = this.tender || (this.opportunity && this.opportunity.tender);
      return Boolean(tender) && tender.status === 'active';
    },
    noticeUrl() {
      const tender = this.opportunity && this.opportunity.tender;
      return tender && tender.source ? tender.source.url : null;
    },
    isAvailable() {
      return Boolean(desktopSeaoRobot()) && isSeaoNoticeUrl(this.noticeUrl);
    },
    progressLabel() {
      if (!this.progress) return this.$t('desktop-robot.signing-in');
      return this.progress;
    },
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    ...mapActions('opportunityDocuments', ['addDocuments']),
    onRobotEvent(event) {
      if (!event || event.type !== 'status') return;
      const phases = {
        'signing-in': 'desktop-robot.signing-in',
        listing: 'desktop-robot.listing',
        downloading: 'desktop-robot.downloading',
      };
      if (phases[event.phase]) this.progress = this.$t(phases[event.phase]);
    },
    async downloadDocuments() {
      const robot = desktopSeaoRobot();
      if (!robot || this.running || !this.isTenderActive) return;
      const status = await robot.status();
      if (!status.hasCredentials) {
        this.$toast.warning(this.$t('desktop-robot.no-credentials'));
        return;
      }
      this.running = true;
      this.progress = null;
      this.unsubscribe = robot.onEvent(this.onRobotEvent);
      try {
        const result = await robot.download(this.noticeUrl);
        this.progress = this.$t('desktop-robot.uploading');
        const { uploaded, skipped } = await this.uploadDownloaded(robot, result.documents);
        if (skipped > 0) this.$toast.show(this.$tc('desktop-robot.not-pdf', skipped, { count: skipped }));
        if (uploaded > 0) {
          this.$toast.success(this.$tc('desktop-robot.uploaded', uploaded, { count: uploaded }));
          this.$emit('documents-uploaded');
        } else {
          this.$toast.show(this.$t('desktop-robot.nothing-new'));
        }
        if (result.failed > 0)
          this.$toast.error(this.$tc('desktop-robot.failed', result.failed, { count: result.failed }));
        if (result.restricted > 0)
          this.$toast.show(this.$tc('desktop-robot.restricted', result.restricted, { count: result.restricted }));
      } catch (error) {
        this.$toast.error(this.$t('desktop-robot.error', { message: error.message }));
      } finally {
        if (this.unsubscribe) this.unsubscribe();
        this.unsubscribe = null;
        this.running = false;
        this.progress = null;
      }
    },
    /**
     * Uploads what the robot has on disk and the opportunity lacks. Only PDFs,
     * like the page's own upload; the rest stay in the desktop's folder.
     */
    async uploadDownloaded(robot, robotDocuments) {
      const missing = documentsToUpload(robotDocuments, this.opportunity.documents);
      const files = [];
      let skipped = 0;
      for (const doc of missing) {
        const file = await robot.readDocument(this.noticeUrl, doc.id);
        const upload = new File([file.data], uploadName(doc.title, file.name), { type: file.type });
        if (isPdfFile(upload)) files.push(upload);
        else skipped++;
      }
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach((file) => formData.append('file[]', file));
        await this.addDocuments({ opportunity: this.opportunity, formData });
      }
      return { uploaded: files.length, skipped };
    },
  },
};
</script>

<style lang="scss">
.desktop-download-documents {
  display: flex;
  align-items: center;
  gap: 4px;

  &__progress {
    color: $color-normal-pale;
    font-size: 12px;
    white-space: nowrap;
  }
}
</style>
